// @ts-strict-ignore
import React, { createContext, useCallback, useContext, useEffect, useState } from "react";

import type { Developer } from "@dashboardApi/apis/developer";

import { useBrazeDashboardConfig } from "@services/brazeDashboardConfig";
import { useCompaniesCompanyIdDevelopersCurrentApiQuery } from "@services/useCompaniesCompanyIdDevelopersCurrentApiQuery";
import { useFeatureOn } from "@services/useFeatureOn";
import { useDeveloperViewModel } from "@services/viewModel/useDeveloperViewModel";
import { useViewModelClasses } from "@services/viewModel/useViewModelClasses";

import { createPermissionsMap } from "./createPermissionsMap";
import type { CurrentDeveloperContextValue, DeveloperWithPermissionMap } from "./types";

const CurrentDeveloperContext = createContext<CurrentDeveloperContextValue | null>(null);

/**
 * Fetches the current developer once and makes it available to all descendants via context.
 * Must be rendered inside QueryClientProvider, BrazeDashboardConfigProvider, and ViewModelClassesProvider.
 */
const CurrentDeveloperProviderInternal = ({ children }: { children: React.ReactNode }) => {
  const user = useBrazeDashboardConfig("user");
  const [currentDeveloperWithPermissionMap, setCurrentDeveloperWithPermissionMap] =
    useState<DeveloperWithPermissionMap>(() => ({
      ...user.developer,
      permissionMap: createPermissionsMap(user.developer.permissions),
    }));
  const { DeveloperViewModel } = useViewModelClasses();
  const [developerViewModel, setDeveloperViewModel] = useDeveloperViewModel();

  const info = useCompaniesCompanyIdDevelopersCurrentApiQuery<DeveloperWithPermissionMap>({
    // when app group ID changes, don't clear the `data` field, since developer does not vary from app group to app group
    refetchInterval: 60 * 1000,
    keepPreviousData: true,
    notifyOnChangeProps: ["data"],
    placeholderData: currentDeveloperWithPermissionMap,
    select: useCallback(
      (developer) => ({ ...developer, permissionMap: createPermissionsMap(developer.permissions) }),
      []
    ),
  });

  const apiDeveloperWithPermissionMap = info.data;
  useEffect(() => {
    if (!apiDeveloperWithPermissionMap) {
      return;
    }
    const apiDeveloper: Developer = (() => {
      const { permissionMap, ...rest } = apiDeveloperWithPermissionMap;
      return rest;
    })();

    const currentDeveloper: Developer = (() => {
      const { permissionMap, ...rest } = currentDeveloperWithPermissionMap;
      return rest;
    })();

    if (DeveloperViewModel && JSON.stringify(apiDeveloper) !== JSON.stringify(currentDeveloper)) {
      setCurrentDeveloperWithPermissionMap(apiDeveloperWithPermissionMap);
      user.developer = apiDeveloper;
      if (developerViewModel) {
        const newDeveloperViewModel = DeveloperViewModel.fromAjaxData(apiDeveloper);
        newDeveloperViewModel.allowedTerritoriesByAppGroupIdByPermission(
          developerViewModel.allowedTerritoriesByAppGroupIdByPermission()
        );
        setDeveloperViewModel(newDeveloperViewModel);
      }
    }
  }, [
    DeveloperViewModel,
    currentDeveloperWithPermissionMap,
    setCurrentDeveloperWithPermissionMap,
    apiDeveloperWithPermissionMap,
    developerViewModel,
    setDeveloperViewModel,
    user,
  ]);

  const value: CurrentDeveloperContextValue = {
    ...info,
    data: currentDeveloperWithPermissionMap,
  };

  return <CurrentDeveloperContext.Provider value={value}>{children}</CurrentDeveloperContext.Provider>;
};

/**
 * Returns the current developer and query state from the nearest CurrentDeveloperProvider.
 * The `data` field is always defined — it falls back to the dashboard config on error.
 */
export const useCurrentDeveloperContext = ({ enabled }: { enabled: boolean }): CurrentDeveloperContextValue => {
  const context = useContext(CurrentDeveloperContext);

  if (!enabled) {
    return null;
  }

  if (!context) {
    throw new Error("useCurrentDeveloperContext must be used within a CurrentDeveloperProvider");
  }
  return context;
};

export const CurrentDeveloperProvider = ({ children }: { children: React.ReactNode }) => {
  const featureOn = useFeatureOn();
  const useContext = featureOn("current_developer_context");

  return useContext ? (
    <CurrentDeveloperProviderInternal>{children}</CurrentDeveloperProviderInternal>
  ) : (
    <>{children}</>
  );
};
